import { Fragment, useCallback, useContext, useState } from 'react';
import { toast } from 'react-toastify';
import { useDebounce } from 'react-use';
import { useFormContext } from 'react-hook-form';
import { InfoBanner } from '@ui';
import { MAX_BLOCKED_LEG_FLIGHT_HOURS } from '@lp/features/RoutePlanner/config';
import { BaseAviaLegSchemaValues } from '@lp/features/RoutePlanner/components/RouteLegForm/validation';
import {
  AirportLocationContext,
  getAirportData,
} from '@lp/features/RoutePlanner/components/RouteLegForm/context/AirportLocationContext';
import { useLegFormContext } from '@lp/features/RoutePlanner/components/RouteLegForm/context/LegFormContext';
import { AirportType } from '@/api/apis/facilities';
import { getRefuelAirportsList } from '@/queries/mission-planner/mission-planner.fetcher';
import { useWatchFields } from '@/hooks/useWatchFields';

export const RefuelAirportsBanner = () => {
  const { isMainDataEdit } = useLegFormContext();
  const { handleSelectedAirport } = useContext(AirportLocationContext);
  const [refuelAirports, setRefuelAirports] = useState<AirportType[]>([]);
  const { setValue } = useFormContext<BaseAviaLegSchemaValues>();
  const { departureLocation, arrivalLocation, timeDurationInMs } = useWatchFields<BaseAviaLegSchemaValues>([
    'departureLocation',
    'arrivalLocation',
    'timeDurationInMs',
  ] as const);

  const fetchRefuelAirports = useCallback(async () => {
    try {
      const airports = await getRefuelAirportsList({
        fromAirportMaoId: Number(departureLocation.maoId),
        toAirportMaoId: Number(arrivalLocation.maoId),
      });

      setRefuelAirports(airports);
    } catch (e) {
      toast.error("Can't fetch refuel airports.");
    }
  }, [arrivalLocation.maoId, departureLocation.maoId]);

  useDebounce(
    () => {
      setRefuelAirports([]);

      if (timeDurationInMs >= MAX_BLOCKED_LEG_FLIGHT_HOURS) {
        fetchRefuelAirports();
      }
    },
    100,
    [timeDurationInMs, fetchRefuelAirports],
  );

  const handleRefuelAirportSelect = async (airport: AirportType) => {
    const airportData = await getAirportData(airport.id);

    if (airportData) {
      setValue('arrivalLocation.id', airportData.value);
      await handleSelectedAirport(airportData);
    }
  };

  if (!refuelAirports?.length) return null;

  return (
    <InfoBanner iconType="info">
      <div className="flex gap-1 text-m-r">
        <span>The refueling airport option is</span>
        <span className="text-m-sb">{departureLocation.displayName} - </span>
        {refuelAirports.map((airport) => (
          <Fragment key={airport.id}>
            <button
              type="button"
              className="underline text-m-sb cursor-pointer"
              disabled={!isMainDataEdit}
              onClick={() => handleRefuelAirportSelect(airport)}
            >
              {airport.iata || airport.icao}
            </button>
            <span> - </span>
          </Fragment>
        ))}
        <span className="text-m-sb">{arrivalLocation.displayName}</span>
      </div>
    </InfoBanner>
  );
};
